/**
 * O cabeçalho da mesa única (E201.F1, GUS-47, ADR-0144; E205.F1.T03, ADR-0147 §7).
 *
 * - Linha 1: o código do caso, as CCBs e o título; à direita, as ações em ordem de importância:
 *   "Conferir e assinar" (primário, travado com texto aberto, rascunho ou gravação em curso no
 *   documento), "Enviar documentos" (abre a aba Documentos com o envio) e o "Mais" (conversa,
 *   histórico, resultado, "Informações da leitura" e "Protocolo e documentos").
 * - Linha 2: os dados do caso em campos rotulados (`dadosDoCaso`).
 *
 * E205.F1: saiu o caminho de pão ("Casos" está na barra lateral); "Documentos" e "Prévia (PDF)", que
 * abrem o painel ao lado, foram para a barra da aba Exame.
 */
import { useRef } from 'react';
import { Link } from 'react-router-dom';

import { Button, buttonVariants } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CaseRecordData } from '@/lib/cases';
import { cn } from '@/lib/utils';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';
import { exigeConclusao } from '@/lib/exame_eletronico_ccb';
import type { ProximoPasso } from '@/lib/resultadoAuditado';

import { CAMPOS_DO_CASO, type DadosDoCaso } from './dadosDoCaso';

const t = ptBR.mesaUnica;
const td = ptBR.documentoDaMesa.fim;

/** Os números das CCBs do caso, sem o documento de apoio e sem repetir. */
export function numerosDasCcbs(ccbs: readonly ExameEletronicoCcbData[] | null): string[] {
  const numeros = (ccbs ?? [])
    .filter(exigeConclusao)
    .map((c) => c.ccb_number)
    .filter((n): n is string => n !== null && n.trim() !== '');
  return [...new Set(numeros)];
}

/** O que o "Mais" guarda: as telas que não são a jornada do exame e o protocolo do caso. */
function MenuMais({
  caseId,
  onProtocolo,
}: {
  caseId: string;
  onProtocolo: () => void;
}): JSX.Element {
  const ref = useRef<HTMLDetailsElement>(null);
  const fechar = (): void => {
    if (ref.current !== null) ref.current.open = false;
  };
  const links = [
    { para: `/casos/${caseId}/conversa`, texto: t.acoes.conversa, id: 'conversa' },
    { para: `/casos/${caseId}/historico`, texto: t.acoes.historico, id: 'historico' },
    { para: `/casos/${caseId}/resultado`, texto: t.acoes.verResultado, id: 'resultado' },
    // E205.F4.T05 (ADR-0147 §2.5): a ficha da leitura, inteira, fora da mesa.
    {
      para: `/casos/${caseId}/informacoes-da-leitura`,
      texto: ptBR.documentosDoCaso.informacoes.acao,
      id: 'informacoes-da-leitura',
    },
  ];
  const item =
    'block w-full px-sc-3 py-sc-2 text-left text-body-sm text-fg no-underline hover:bg-fg/[0.05]';
  return (
    <details ref={ref} className="relative" data-testid="mesa-acao-mais">
      <summary
        className={cn(buttonVariants({ variant: 'secondary' }), 'cursor-pointer list-none')}
        data-testid="mesa-acao-mais-abrir"
      >
        {t.acoes.mais}
      </summary>
      <div
        className="absolute right-0 z-20 mt-sc-1 min-w-[14rem] border border-border bg-bg py-sc-1 shadow-sm"
        data-testid="mesa-acao-mais-menu"
      >
        {links.map((a) => (
          <Link
            key={a.id}
            to={a.para}
            className={item}
            onClick={fechar}
            data-testid={`mesa-acao-${a.id}`}
          >
            {a.texto}
          </Link>
        ))}
        <button
          type="button"
          className={item}
          onClick={() => {
            fechar();
            onProtocolo();
          }}
          data-testid="mesa-acao-protocolo"
        >
          {t.acoes.protocoloEDocumentos}
        </button>
      </div>
    </details>
  );
}

/** "Conferir e assinar": o passo final da jornada, travado enquanto o documento tem texto aberto. */
function ConferirEAssinar({
  caseId,
  pendencia,
}: {
  caseId: string;
  pendencia: string | null;
}): JSX.Element {
  if (pendencia !== null) {
    return (
      <Button
        type="button"
        disabled
        title={pendencia}
        aria-describedby="mesa-conferir-motivo"
        data-testid="mesa-acao-conferir-e-assinar"
      >
        {td.conferir}
        <span id="mesa-conferir-motivo" className="sr-only">
          {pendencia}
        </span>
      </Button>
    );
  }
  return (
    <Link
      to={`/casos/${caseId}/conferencia-final`}
      className={cn(buttonVariants({ variant: 'primary' }), 'no-underline')}
      data-testid="mesa-acao-conferir-e-assinar"
    >
      {td.conferir}
    </Link>
  );
}

export interface AcoesDaMesaProps {
  caseId: string;
  /** Por que ainda não dá para conferir e assinar (`pendenciaDoDocumento`); `null`, pode. */
  pendencia: string | null;
  /** Abre a aba Documentos com o envio aberto. */
  onEnviarDocumentos: () => void;
  /** Mostra "Protocolo e documentos", fora da tela por padrão. */
  onProtocolo: () => void;
}

function Acoes(props: AcoesDaMesaProps): JSX.Element {
  return (
    <nav aria-label={t.acoes.rotulo} className="flex flex-wrap items-start gap-sc-2">
      <ConferirEAssinar caseId={props.caseId} pendencia={props.pendencia} />
      <Button
        type="button"
        variant="secondary"
        onClick={props.onEnviarDocumentos}
        data-testid="mesa-acao-enviar-documentos"
      >
        {t.acoes.enviarDocumentos}
      </Button>
      <MenuMais caseId={props.caseId} onProtocolo={props.onProtocolo} />
    </nav>
  );
}

/** A grade dos dados do caso: rótulo em caption, valor em corpo; o que falta é "—". */
function DadosDoCasoEmGrade({ dados }: { dados: DadosDoCaso }): JSX.Element {
  return (
    <dl
      aria-label={t.dadosDoCaso.rotulo}
      className="mt-sc-4 grid grid-cols-2 gap-x-sc-6 gap-y-sc-2 border-t border-border pt-sc-3 md:grid-cols-[repeat(5,minmax(0,auto))] md:justify-start"
      data-testid="mesa-dados-do-caso"
    >
      {CAMPOS_DO_CASO.map((campo) => (
        <div key={campo} className="min-w-0" data-testid={`mesa-dado-${campo}`}>
          <dt className="text-caption uppercase tracking-[0.08em] text-fg-muted">
            {t.dadosDoCaso[campo]}
          </dt>
          <dd className="truncate text-body-sm text-fg" title={dados[campo] ?? undefined}>
            {dados[campo] ?? t.dadosDoCaso.semValor}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** E205.F3: o próximo passo que segura a emissão, num chip que abre a aba Pendências. */
function ChipDoProximo({ proximo }: { proximo: ProximoDoCabecalho }): JSX.Element {
  return (
    <button
      type="button"
      onClick={proximo.onAbrir}
      title={t.pendencias.chipRotulo}
      className="mt-sc-2 inline-flex items-center border border-accent-200 bg-accent-50 px-sc-2 py-1 text-caption font-medium text-accent-700 hover:bg-accent-100"
      data-testid="mesa-chip-proximo"
    >
      {t.pendencias.chip.replace('{passo}', ptBR.resultadoAuditado.proximoPasso[proximo.passo])}
    </button>
  );
}

export interface ProximoDoCabecalho {
  passo: ProximoPasso;
  onAbrir: () => void;
}

export function CabecalhoDaMesa({
  caso,
  ccbs,
  dados,
  proximo = null,
  ...acoes
}: AcoesDaMesaProps & {
  caso: CaseRecordData | null;
  ccbs: readonly ExameEletronicoCcbData[] | null;
  dados: DadosDoCaso;
  proximo?: ProximoDoCabecalho | null;
}): JSX.Element {
  const numeros = numerosDasCcbs(ccbs);
  return (
    <header data-testid="mesa-cabecalho">
      <div className="flex flex-wrap items-start justify-between gap-sc-4">
        <div className="min-w-0">
          <h1 id="mesa-unica-title" className="text-display-md text-fg" data-testid="mesa-codigo">
            {caso?.display_code ?? t.carregando}
            {numeros.length > 0 ? (
              <span
                className="ml-sc-2 text-body-md font-normal text-fg-muted"
                data-testid="mesa-ccbs"
              >
                · {numeros.map((n) => t.ccb.replace('{numero}', n)).join(' · ')}
              </span>
            ) : null}
          </h1>
          {caso !== null && caso.title.trim() !== '' ? (
            <p className="mt-1 text-body-md text-fg-muted" data-testid="mesa-titulo-do-caso">
              {caso.title}
            </p>
          ) : null}
          {proximo !== null ? <ChipDoProximo proximo={proximo} /> : null}
        </div>
        <Acoes {...acoes} />
      </div>
      <DadosDoCasoEmGrade dados={dados} />
    </header>
  );
}
